import React, { createContext, useContext, useState, useEffect } from 'react';
import baseFetchWithAuth from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';

const InterventionPointContext = createContext();

export function InterventionPointProvider({ children }) {
  // Project selection
  const [projets, setProjets] = useState([]);
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectSelectionAttempted, setProjectSelectionAttempted] = useState(false);

  // Data
  const [interventionPoints, setInterventionPoints] = useState([]);
  const [filteredInterventionPoints, setFilteredInterventionPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastLoadedProjectId, setLastLoadedProjectId] = useState(null); // Track which project's data is currently loaded

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Filter logic
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setFilteredInterventionPoints([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = interventionPoints.filter(ip => {
      const name = ip.Name?.toLowerCase() || '';
      const details = ip.Details?.toLowerCase() || '';
      const shopId = ip.ShopID?.toString().toLowerCase() || '';

      return name.includes(query) || details.includes(query) || shopId.includes(query);
    });

    setFilteredInterventionPoints(filtered);
  }, [interventionPoints, searchQuery]);

  // Fetch IPs when project changes
  useEffect(() => {
    const fetchIPs = async () => {
      if (!selectedProjet?.value) {
        setInterventionPoints([]);
        return;
      }

      setLoading(true);
      try {
        // The backend route /ips currently returns everything for the hardcoded project query.
        // We might need to pass the project ID if the backend supported it, but for now it's hardcoded in SQL.
        // We'll call it anyway.
        const response = await baseFetchWithAuth(`${API_BASE_URL}/api/dwh/one_mag/ips?projectId=${selectedProjet.value}`);
        if (Array.isArray(response)) {
          setInterventionPoints(response);
          setLastLoadedProjectId(selectedProjet.value);
        } else {
          console.error("Expected array of IPs, got:", response);
          setInterventionPoints([]);
          setLastLoadedProjectId(null);
        }
      } catch (error) {
        console.error("Error fetching IPs:", error);
        setInterventionPoints([]);
        setLastLoadedProjectId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchIPs();
  }, [selectedProjet]);

  const [selectedInterventionPoint, setSelectedInterventionPoint] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch IP details when selected
  useEffect(() => {
    const fetchDetails = async () => {
      if (!selectedInterventionPoint) return;

      // If we already have details (CallIDs), no need to fetch unless we want to refresh
      // But the list only has CallCount now.

      setLoadingDetails(true);
      try {
        const response = await baseFetchWithAuth(`${API_BASE_URL}/api/dwh/one_mag/ips/${encodeURIComponent(selectedInterventionPoint.Name)}?projectId=${selectedProjet.value}`);
        if (Array.isArray(response)) {
             setSelectedInterventionPoint(prev => ({
                 ...prev,
                 CallIDs: response
             }));
        }
      } catch (error) {
        console.error("Error fetching IP details:", error);
      } finally {
        setLoadingDetails(false);
      }
    };

    if (selectedInterventionPoint && !selectedInterventionPoint.CallIDs) {
        fetchDetails();
    }
  }, [selectedInterventionPoint]);

  // Read search params on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');
    const storeId = params.get('storeId'); // Assuming Name or ShopID to pre-select
    const query = params.get('q');

    // We can set search query immediately
    if (query) {
        setSearchQuery(query);
    }

    // For project, we need to wait until projects are loaded
    // This logic is tricky if projects are loaded inside the header component or context.
    // Here `projets` is in context but loaded by header...
    // Ideally header should load projects and set them in context, or context should load them.
    // Currently Header loads them. We might need to move project loading here or watch `projets` state.
  }, []);

  // Watch for projects to be loaded to select the one from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');

    if (projectsLoaded) {
        if (projectId && !selectedProjet && projets.length > 0) {
            const found = projets.find(p => String(p.value) === String(projectId));
            if (found) {
                setSelectedProjet(found);
            }
        }
        setProjectSelectionAttempted(true);
    }
  }, [projets, projectsLoaded]);

  // Watch for IPs to be loaded to select the store from URL
  useEffect(() => {
     const params = new URLSearchParams(window.location.search);
     const storeId = params.get('storeId');

     // Try to select if we have IPs and the loaded data matches selected project
     if (storeId && selectedProjet && lastLoadedProjectId === selectedProjet.value && interventionPoints.length > 0 && !selectedInterventionPoint) {
         // Check for Name or ShopID
         const found = interventionPoints.find(ip => ip.Name === storeId || String(ip.ShopID) === String(storeId));
         if (found) {
             setSelectedInterventionPoint(found);
         }
     }
  }, [interventionPoints, selectedProjet, lastLoadedProjectId]);

  // Update URL when state changes
  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      let changed = false;

      if (selectedProjet) {
          if (params.get('projectId') !== String(selectedProjet.value)) {
              params.set('projectId', selectedProjet.value);
              changed = true;
          }
      } else if (params.has('projectId')) {
          // Only delete if we have attempted selection (data loaded)
          if (projectSelectionAttempted) {
              params.delete('projectId');
              changed = true;
          }
      }

      if (selectedInterventionPoint) {
           if (params.get('storeId') !== selectedInterventionPoint.Name) {
               params.set('storeId', selectedInterventionPoint.Name);
               changed = true;
           }
      } else if (params.has('storeId')) {
          // Only delete if we have loaded data for the current project
          // This prevents deleting the param while data is loading or if project mismatch
          if (selectedProjet && lastLoadedProjectId === selectedProjet.value) {
              params.delete('storeId');
              changed = true;
          }
      }

      if (searchQuery) {
          if (params.get('q') !== searchQuery) {
              params.set('q', searchQuery);
              changed = true;
          }
      } else if (params.has('q')) {
          params.delete('q');
          changed = true;
      }

      if (changed) {
          const newUrl = `${window.location.pathname}?${params.toString()}`;
          window.history.replaceState({}, '', newUrl);
      }
  }, [selectedProjet, selectedInterventionPoint, searchQuery, projectSelectionAttempted, lastLoadedProjectId]);

  const value = {
    projets,
    setProjets,
    selectedProjet,
    setSelectedProjet,
    searchQuery,
    setSearchQuery,
    interventionPoints,
    filteredInterventionPoints,
    loading,
    selectedInterventionPoint,
    setSelectedInterventionPoint,
    loadingDetails,
    projectsLoaded, // Expose this
    setProjectsLoaded // And this
  };

  return (
    <InterventionPointContext.Provider value={value}>
      {children}
    </InterventionPointContext.Provider>
  );
}

export function useInterventionPoint() {
  const context = useContext(InterventionPointContext);

  if (!context)
    throw new Error('useInterventionPoint must be used within a InterventionPointProvider');

  return context;
}
